// File: ScheduleTooltip.tsx
import { createPortal } from 'react-dom';
import styles from './ScheduleTooltip.module.css';
import { extractScheduleAssignments, formatTimeRanges, formatDate } from '../utils/scheduleTooltipUtils';

/** Schedule-Zeile, wie sie die Students-Schedules-API liefert (inkl. teacher_name-Join). */
interface TooltipSchedule {
  class_id?: string | null;
  is_primary?: boolean | null;
  name?: string | null;
  created_at?: string | null;
  teacher_name?: string | null;
  available_times?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface ScheduleTooltipProps {
  schedules?: readonly TooltipSchedule[] | null;
  studentMemberIds?: readonly string[] | null;
  /** Cursor-Position. */
  position?: { x: number; y: number } | null;
  /** ID der aktuellen Klasse (wird herausgefiltert). */
  currentClassId?: string | null;
  /** class_ids mit primärem Stundenplan. */
  classesWithPrimary?: readonly string[];
}

/**
 * Tooltip-Komponente, die Schedule-Informationen beim Hovern anzeigt
 */
export default function ScheduleTooltip({ schedules, studentMemberIds, position, currentClassId, classesWithPrimary = [] }: ScheduleTooltipProps) {
  if (!schedules || schedules.length === 0 || !studentMemberIds || studentMemberIds.length === 0 || !position) {
    return null;
  }

  // Filtere Schedules der aktuellen Klasse heraus
  const otherSchedules = schedules.filter(schedule => schedule.class_id !== currentClassId);

  if (otherSchedules.length === 0) {
    return null;
  }

  // Filter schedules: ONLY show primary schedules
  // If no primary schedule exists for a class, no conflicts are shown for that class
  const schedulesToShow = otherSchedules.filter(schedule => schedule.is_primary === true);

  if (schedulesToShow.length === 0) {
    return null;
  }

  // Extrahiere Zuordnungen from filtered schedules
  const scheduleAssignments = extractScheduleAssignments(schedulesToShow, studentMemberIds);

  if (scheduleAssignments.length === 0) {
    return null;
  }

  // Portal für z-index-Management
  const tooltipContent = (
    <div
      className={styles.tooltip}
      style={{
        left: `${position.x + 10}px`,
        top: `${position.y + 10}px`
      }}
    >
      <div className={styles.header}>Bereits vergeben in anderen Kursen:</div>
      <div className={styles.schedulesList}>
        {scheduleAssignments.map((item, index) => {
          const schedule = item.schedule;
          const timeRanges = formatTimeRanges(item.assignments);
          const formattedDate = formatDate(schedule.created_at);

          return (
            <div key={index} className={styles.scheduleItem}>
              <div className={styles.scheduleHeader}>
                <span className={styles.scheduleName}>{schedule.name || 'Unbenannter Stundenplan'}</span>
                <div className={styles.scheduleHeaderRight}>
                  {formattedDate && (
                    <span className={styles.scheduleDate}>{formattedDate}</span>
                  )}
                  <span className={styles.scheduleTeacher}>{schedule.teacher_name || 'Unbekannt'}</span>
                </div>
              </div>
              {timeRanges.length > 0 && (
                <div className={styles.timeRanges}>
                  {timeRanges.map((range, rangeIndex) => (
                    <span key={rangeIndex} className={styles.timeRange}>
                      {range}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return createPortal(tooltipContent, document.body);
}
